import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TagColor } from '@church/domain'
import { useApi } from '../api-context.tsx'
import { eventKeys } from './useEvents.ts'

/**
 * The church's event tags, in groups — what the calendar is filtered by and
 * what an event is labelled with. Changing the list invalidates every event
 * query too, because a renamed or removed tag changes the chips they show.
 */

export function useEventTags(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: eventKeys.tags(),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.events.tags.get()
      if (error) throw error
      return data
    },
  })
}

export type EventTagGroup = NonNullable<ReturnType<typeof useEventTags>['data']>[number]
export type EventTag = EventTagGroup['tags'][number]

export function useEventTagMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: eventKeys.all() })

  const createGroup = useMutation({
    mutationFn: async (body: { name: string }) => {
      const { data, error } = await api.api.v1.events.tags.groups.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
  const renameGroup = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await api.api.v1.events.tags.groups({ id }).patch({ name })
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
  const removeGroup = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.events.tags.groups({ id }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })
  const createTag = useMutation({
    mutationFn: async (body: { groupId: string; name: string; color?: TagColor | null }) => {
      const { data, error } = await api.api.v1.events.tags.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
  const updateTag = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; color?: TagColor | null }) => {
      const { data, error } = await api.api.v1.events.tags({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
  const removeTag = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.events.tags({ id }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })

  return { createGroup, renameGroup, removeGroup, createTag, updateTag, removeTag }
}

/**
 * The tag filter a calendar screen holds: which tags are picked, toggling
 * one, and the query value (`tags=`) the calendar reads.
 */
export function tagFilterQuery(picked: readonly string[]): string | undefined {
  return picked.length === 0 ? undefined : [...picked].sort().join(',')
}
